import { FC } from 'react';

import TagAutomationActionConfigFieldset from '@/ui/components/automationActionConfigFieldset/components/TagAutomationActionConfigFieldset';
import CreateProjectProgressionAutomationActionConfigFieldset from '@/ui/components/automationActionConfigFieldset/CreateProjectTaskAutomationActionConfigFieldset';
import {
  ActionConfigHelperType,
  AutomationActionTypes,
  AutomationCondition,
  AutomationEntityTypes,
  CreateProjectTaskActionConfig,
  CreateRepeatProjectActionConfig,
  EmailNotificationActionConfig,
  ProgressProjectToPipelineStageActionConfig,
  SMSActionConfig,
  TagActionConfig,
} from '@payaca/types/automationTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { Upload } from '@payaca/types/uploadTypes';
import './AutomationActionConfigFieldset.sass';
import { CreateRepeatProjectActionConfigFieldset } from './CreateRepeatProjectActionConfigFieldset';
import EmailNotificationAutomationActionConfigFieldset from './EmailNotificationAutomationActionConfigFieldset';
import { ProgressProjectToPipelineStageActionConfigFieldset } from './ProgressProjectToPipelineStageActionConfigFieldset';
import SmsNotificationAutomationActionConfigFieldset from './SmsNotificationAutomationActionConfigFieldset';

type Props<T extends AutomationActionTypes> = {
  conditions: AutomationCondition[];
  type: T;
  config: ActionConfigHelperType[T];
  automationEntityType: AutomationEntityTypes;
  onChange: (changeValue: { [key: string]: any }) => void;
  uploadIds?: Upload['id'][];
  onUploadCollectionChange?: (uploadIds: Upload['id'][]) => void;
  validationState?: Record<string, FieldValidationResult>;
  touchedState?: Record<string, boolean>;
  onTouch?: (fieldName: string) => void;
  showFieldValidationMessagesUnconditionally?: boolean;
};

const AutomationActionConfigFieldset: FC<Props<AutomationActionTypes>> = ({
  conditions,
  type,
  config,
  automationEntityType,
  onChange,
  uploadIds,
  onUploadCollectionChange,
  validationState,
  touchedState,
  onTouch,
  showFieldValidationMessagesUnconditionally,
}) => {
  switch (type) {
    case 'E-mail Notification':
      return (
        <EmailNotificationAutomationActionConfigFieldset
          // Don't feel I should need this typecast, why isn't the type being inferred
          config={config as EmailNotificationActionConfig}
          onChange={onChange}
          automationEntityType={automationEntityType}
          uploadIds={uploadIds}
          onUploadCollectionChange={onUploadCollectionChange}
          validationState={validationState}
          touchedState={touchedState}
          onTouch={onTouch}
          showFieldValidationMessagesUnconditionally={
            showFieldValidationMessagesUnconditionally
          }
        />
      );
    case 'SMS Notification':
      return (
        <SmsNotificationAutomationActionConfigFieldset
          // Don't feel I should need this typecast, why isn't the type being inferred
          config={config as SMSActionConfig}
          onChange={onChange}
          automationEntityType={automationEntityType}
          validationState={validationState}
          touchedState={touchedState}
          onTouch={onTouch}
          showFieldValidationMessagesUnconditionally={
            showFieldValidationMessagesUnconditionally
          }
        />
      );
    case 'Move Project to Pipeline stage':
      return (
        <ProgressProjectToPipelineStageActionConfigFieldset
          conditions={conditions}
          config={config as ProgressProjectToPipelineStageActionConfig}
          onChange={onChange}
          validationState={validationState}
          touchedState={touchedState}
          onTouch={onTouch}
          showFieldValidationMessagesUnconditionally={
            showFieldValidationMessagesUnconditionally
          }
        />
      );
    case 'Create repeat Project':
      return (
        <CreateRepeatProjectActionConfigFieldset
          config={config as CreateRepeatProjectActionConfig}
          onChange={onChange}
          onTouch={onTouch}
        />
      );
    case 'Tag Project':
    case 'Tag Event':
    case 'Remove Project tags':
    case 'Remove Event tags':
      return (
        <TagAutomationActionConfigFieldset
          config={config as TagActionConfig}
          onChange={onChange}
          validationState={validationState}
          touchedState={touchedState}
          onTouch={onTouch}
          showFieldValidationMessagesUnconditionally={
            showFieldValidationMessagesUnconditionally
          }
        />
      );
    case 'Create Project Task':
      return (
        <CreateProjectProgressionAutomationActionConfigFieldset
          config={config as CreateProjectTaskActionConfig}
          onChange={onChange}
          validationState={validationState}
          touchedState={touchedState}
          onTouch={onTouch}
          showFieldValidationMessagesUnconditionally={
            showFieldValidationMessagesUnconditionally
          }
        />
      );
    default:
      return null;
  }
};

export default AutomationActionConfigFieldset;
